import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";
import { isSupabaseConnected, fetchJournalEntries } from "./supabase";
import type { JournalEntry } from "./admin/AdminDataContext";

function mapRowToEntry(row: {
  id: number | string;
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  image?: string | null;
  author?: string | null;
  published?: boolean;
  i18n?: Record<string, unknown> | null;
  created_at?: string | null;
}): JournalEntry {
  return {
    id: Number(row.id),
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt ?? "",
    content: row.content ?? "",
    image: row.image ?? "",
    author: row.author ?? "",
    published: row.published ?? true,
    i18n: (row.i18n ?? {}) as JournalEntry["i18n"],
    created_at: row.created_at ?? new Date().toISOString(),
  };
}

/** Gentle, zen-like transition (no auto-play); starts on the most recent post. */
const CAROUSEL_DURATION_MS = 720;
const CAROUSEL_EASING = "cubic-bezier(0.25, 0.1, 0.25, 1)";

export function JournalCarouselSection() {
  const { journal: contextJournal } = useAdminData();
  const { lang, t } = useLanguage();
  const theme = useTheme();
  const [published, setPublished] = useState<JournalEntry[]>(() =>
    isSupabaseConnected() ? [] : contextJournal.filter((j) => j.published)
  );
  const [loading, setLoading] = useState(isSupabaseConnected());
  // Start on the latest post (index 0 = most recent when ordered by created_at desc)
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isSupabaseConnected()) {
      setPublished(contextJournal.filter((j) => j.published));
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchJournalEntries()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch journal:", error.message);
          setPublished(contextJournal.filter((j) => j.published));
          return;
        }
        setPublished((data ?? []).map(mapRowToEntry));
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSupabaseConnected()) return;
    setPublished(contextJournal.filter((j) => j.published));
  }, [contextJournal]);

  // Keep index in bounds when list changes
  useEffect(() => {
    if (published.length === 0) return;
    setCurrentIndex((i) => Math.min(i, published.length - 1));
  }, [published.length]);

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(published.length - 1, i + 1));
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < published.length - 1 && published.length > 1;

  if (loading || published.length === 0) {
    if (loading) {
      return (
        <section id="journal" className="py-24" style={{ backgroundColor: `${theme.border}30` }}>
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}>Loading…</p>
          </div>
        </section>
      );
    }
    return null;
  }

  const entry = published[currentIndex];

  return (
    <section id="journal" className="py-24 overflow-hidden" style={{ backgroundColor: `${theme.border}30` }}>
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p
            className="tracking-[0.3em] uppercase mb-2"
            style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
          >
            {t("journal.label")}
          </p>
          <h2
            className="tracking-[0.08em]"
            style={{ fontFamily: theme.fontHeading, fontSize: "clamp(26px, 3.5vw, 38px)", fontWeight: 400, color: theme.fg }}
          >
            {t("journal.title")}
          </h2>
        </div>

        <div className="relative overflow-hidden">
          {/* Track: one slide visible at a time; gentle horizontal slide */}
          <div
            className="flex transition-transform ease-out"
            style={{
              width: `${published.length * 100}%`,
              transform: `translateX(-${(currentIndex * 100) / published.length}%)`,
              transitionDuration: `${CAROUSEL_DURATION_MS}ms`,
              transitionTimingFunction: CAROUSEL_EASING,
            }}
          >
            {published.map((e) => (
              <div
                key={e.id}
                className="shrink-0 px-2"
                style={{ width: `${100 / published.length}%` }}
              >
                <article className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                  <div className="overflow-hidden rounded-xl" style={{ backgroundColor: `${theme.border}40` }}>
                    {e.image ? (
                      <ImageWithFallback
                        src={e.image}
                        alt={localized(e, "title", lang)}
                        className="w-full aspect-[4/3] object-cover"
                      />
                    ) : (
                      <div className="w-full aspect-[4/3] flex items-center justify-center" style={{ backgroundColor: theme.border }}>
                        <span style={{ fontFamily: theme.fontBody, fontSize: "13px", color: theme.muted }}>—</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p
                      className="tracking-[0.2em] uppercase mb-2"
                      style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.accent }}
                    >
                      {e.author} · {new Date(e.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                    <h3
                      className="tracking-[0.06em] mb-4"
                      style={{ fontFamily: theme.fontHeading, fontSize: "clamp(20px, 2.5vw, 28px)", fontWeight: 400, color: theme.fg }}
                    >
                      {localized(e, "title", lang)}
                    </h3>
                    <p
                      className="leading-[1.8] mb-6 line-clamp-4"
                      style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
                    >
                      {localized(e, "excerpt", lang)}
                    </p>
                    <Link
                      to={`/journal/${e.slug}`}
                      className="inline-block tracking-[0.15em] uppercase border-b pb-0.5 transition-colors hover:opacity-80"
                      style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.primary, borderColor: `${theme.primary}4d` }}
                    >
                      {t("journal.read_more")}
                    </Link>
                  </div>
                </article>
              </div>
            ))}
          </div>

          {/* Prev / Next buttons */}
          <div className="flex items-center justify-between mt-8 md:mt-10 px-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canGoPrev}
              aria-label="Previous story"
              className="flex items-center justify-center w-12 h-12 rounded-full border transition-all duration-300 disabled:opacity-30 disabled:pointer-events-none hover:scale-[1.02] active:scale-[0.98]"
              style={{
                borderColor: theme.border,
                color: theme.fg,
                backgroundColor: theme.card,
              }}
            >
              <ChevronLeft size={22} strokeWidth={1.5} />
            </button>
            <span
              className="text-center tabular-nums"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.muted }}
            >
              {currentIndex + 1} / {published.length}
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext}
              aria-label="Next story"
              className="flex items-center justify-center w-12 h-12 rounded-full border transition-all duration-300 disabled:opacity-30 disabled:pointer-events-none hover:scale-[1.02] active:scale-[0.98]"
              style={{
                borderColor: theme.border,
                color: theme.fg,
                backgroundColor: theme.card,
              }}
            >
              <ChevronRight size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/journal"
            className="tracking-[0.2em] uppercase transition-opacity hover:opacity-80"
            style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.primary }}
          >
            {t("journal.title")} — {t("journal.more_stories")}
          </Link>
        </div>
      </div>
    </section>
  );
}
